import { PayloadAction, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { _SocietyLinks, Link, Society } from 'types/api.types';
import { _IpiListLinks, Ipi, IpiValidations } from 'types/ipi.types';
import { _IpnListLinks, Ipn, IpnValidations } from 'types/ipn.types';
import { LoadingState } from 'types/enums';
import {
  _ProfileListLinks,
  Profile,
  SearchProfilesValidationProperties,
  Connection,
  Role,
} from 'types/profile';
import { _IsniListLinks, Isni } from 'types/isni.types';
import {
  addIpiManuallyEffect,
  addIpnManuallyEffect,
  createIsniEffect,
  createRepresentativeEffect,
  deleteConnectionEffect,
  deleteIpiEffect,
  deleteIpnEffect,
  deleteIsniEffect,
  deleteProfileImageEffect,
  deleteRepresentativeEffect,
  fetchCmoSocietiesEffect,
  fetchConnectionsEffect,
  fetchIpiValidationsEffect,
  fetchIpnValidationsEffect,
  fetchLegalProfileEffect,
  fetchProfileIdsEffect,
  fetchProfileImageStatusEffect,
  fetchProSocietiesEffect,
  fetchRepresentativeRolesEffect,
  fetchSearchProfilesValidationsEffect,
  updateIpiEffect,
  updateIpnEffect,
  updateIsniEffect,
  updatePrivacySettingsEffect,
  updateProfileEffect,
  updateRepresentativeEffect,
  uploadProfileImageEffect,
} from './effects';

export interface ProfileState {
  profiles: {
    [guid: string]: Profile;
  };
  currentProfileGuid?: string;
  _links?: _ProfileListLinks;
  ipis: Ipi[];
  ipns: Ipn[];
  isnis: Isni[];
  connections: Connection[];
  _ipiListLinks?: _IpiListLinks;
  _ipnListLinks?: _IpnListLinks;
  _isniListLinks?: _IsniListLinks;
  _societyLinks?: _SocietyLinks;
  proSocieties?: Society[];
  cmoSocieties?: Society[];
  ipiValidations?: IpiValidations;
  ipnValidations?: IpnValidations;
  loadingState: LoadingState;
  profileIdsLoadingState: LoadingState;
  searchProfilesValidations?: SearchProfilesValidationProperties;
  representative: {
    roles?: { [key: string]: Role };
  };
  search: {
    loadMore: boolean;
    _links: {
      next?: Link;
    };
  };
}

export const profileInitialState: ProfileState = {
  loadingState: LoadingState.PRISTINE,
  profiles: {},
  ipis: [],
  ipns: [],
  isnis: [],
  connections: [],
  profileIdsLoadingState: LoadingState.PRISTINE,
  representative: {},
  search: {
    loadMore: false,
    _links: {},
  },
};

const slice = createSlice({
  name: 'profile',
  initialState: profileInitialState,
  reducers: {
    setSearchNextLink(state, { payload }: PayloadAction<{ next?: Link }>) {
      state.search._links.next = payload.next;
    },
    setLoadMore(state, { payload }: PayloadAction<boolean>) {
      if (state.search._links.next) {
        state.search.loadMore = payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfileIdsEffect.pending, (state) => {
        state.profileIdsLoadingState = LoadingState.FETCHING;
      })
      .addCase(fetchProfileIdsEffect.fulfilled, (state, { payload }) => {
        state.ipis = payload.ipi.items;
        state.ipns = payload.ipn.items;

        if (payload.isni) {
          state.isnis = payload.isni.items;
          state._isniListLinks = payload.isni._links;
        }

        state._ipiListLinks = payload.ipi._links;
        state._ipnListLinks = payload.ipn._links;
        state.profileIdsLoadingState = LoadingState.FETCHED;
      })
      .addCase(fetchProfileIdsEffect.rejected, (state) => {
        state.profileIdsLoadingState = LoadingState.ERROR;
      })
      .addCase(fetchLegalProfileEffect.pending, (state) => {
        state.loadingState = LoadingState.FETCHING;
      })
      .addCase(fetchLegalProfileEffect.fulfilled, (state, { payload }) => {
        state.profiles[payload.guid] = payload;
        state.loadingState = LoadingState.FETCHED;
        state.currentProfileGuid = payload.guid;
      })
      .addCase(fetchLegalProfileEffect.rejected, (state) => {
        state.loadingState = LoadingState.ERROR;
      })
      .addCase(updateProfileEffect.fulfilled, (state, { payload }) => {
        state.profiles[payload.guid] = payload;
      })
      .addCase(
        fetchProSocietiesEffect.fulfilled,
        (state, { payload: { items, _links } }) => {
          state.proSocieties = items;
          state._societyLinks = { ...state._societyLinks, ..._links };
        }
      )
      .addCase(
        fetchCmoSocietiesEffect.fulfilled,
        (state, { payload: { items, _links } }) => {
          state.cmoSocieties = items;
          state._societyLinks = { ...state._societyLinks, ..._links };
        }
      )
      .addCase(fetchIpiValidationsEffect.fulfilled, (state, { payload }) => {
        state.ipiValidations = payload;
      })
      .addCase(fetchIpnValidationsEffect.fulfilled, (state, { payload }) => {
        state.ipnValidations = payload;
      })
      .addCase(
        addIpiManuallyEffect.fulfilled,
        (state, { payload: { item } }) => {
          const ipiIndex = state.ipis.findIndex(
            (el) => el.ipiNameNumber === item.ipiNameNumber
          );
          if (ipiIndex > -1) {
            state.ipis[ipiIndex] = item;
            return;
          }
          state.ipis.unshift(item);
        }
      )
      .addCase(
        addIpnManuallyEffect.fulfilled,
        (state, { payload: { item } }) => {
          const ipnIndex = state.ipns.findIndex(
            (el) => el.ipnNumber === item.ipnNumber
          );
          if (ipnIndex > -1) {
            state.ipns[ipnIndex] = item;
            return;
          }
          state.ipns.unshift(item);
        }
      )
      .addCase(createIsniEffect.fulfilled, (state, { payload }) => {
        const isniIndex = state.isnis.findIndex(
          (el) => el.isni === payload.item.isni
        );
        if (isniIndex > -1) {
          state.isnis[isniIndex] = payload.item;
          return;
        }
        state.isnis.unshift(payload.item);
      })
      .addCase(updateIsniEffect.fulfilled, (state, { payload }) => {
        const isniToUpdateIndex = state.isnis.findIndex(
          ({ isni }) => isni === payload.item.isni
        );
        state.isnis[isniToUpdateIndex] = payload.item;
      })
      .addCase(updateIpnEffect.fulfilled, (state, { payload }) => {
        const ipnToUpdateIndex = state.ipns.findIndex(
          (ipn) => ipn.ipnNumber === payload.ipnNumber
        );
        state.ipns[ipnToUpdateIndex] = payload;
      })
      .addCase(updateIpiEffect.fulfilled, (state, { payload }) => {
        const ipiToUpdateIndex = state.ipis.findIndex(
          (ipi) => ipi.ipiNameNumber === payload.ipiNameNumber
        );
        state.ipis[ipiToUpdateIndex] = payload;
      })
      .addCase(uploadProfileImageEffect.fulfilled, (state, { payload }) => {
        if (!state.currentProfileGuid) {
          return;
        }
        const profile = state.profiles[state.currentProfileGuid];
        if (profile._links) {
          profile._links.uploadStatus = payload;
        }
      })
      .addCase(deleteIpiEffect.fulfilled, (state, { meta }) => {
        state.ipis = state.ipis.filter(
          (ipi) => ipi.ipiNameNumber !== meta.arg.ipiNameNumber
        );
      })
      .addCase(deleteIpnEffect.fulfilled, (state, { meta }) => {
        state.ipns = state.ipns.filter(
          (ipn) => ipn.ipnNumber !== meta.arg.ipnNumber
        );
      })
      .addCase(deleteIsniEffect.fulfilled, (state, { meta }) => {
        state.isnis = state.isnis.filter(
          (isni) => isni.isni !== meta.arg.isniNumber
        );
      })
      .addCase(
        fetchProfileImageStatusEffect.fulfilled,
        (state, { payload: { images } }) => {
          if (images && state.currentProfileGuid) {
            state.profiles[state.currentProfileGuid].profileImages = images;
          }
        }
      )
      .addCase(deleteProfileImageEffect.fulfilled, (state) => {
        if (state.currentProfileGuid) {
          state.profiles[state.currentProfileGuid].profileImages = [];
        }
      })
      .addCase(
        fetchSearchProfilesValidationsEffect.fulfilled,
        (state, { payload }) => {
          state.searchProfilesValidations = payload;
        }
      )
      .addCase(fetchConnectionsEffect.fulfilled, (state, { payload, meta }) => {
        if (!meta?.arg?.providerId) {
          state.connections = payload;
          return;
        }

        const singleConnection = payload[0];
        const connectionIndex = state.connections.findIndex((connection) => {
          return connection?.providerId === meta.arg?.providerId;
        });

        if (connectionIndex === -1 && singleConnection) {
          state.connections.push(singleConnection);
          return;
        }
        state.connections[connectionIndex] = singleConnection;
      })
      .addCase(deleteConnectionEffect.fulfilled, (state, { meta }) => {
        state.connections = state.connections.filter(
          (connection) => connection.guid !== meta.arg.connectionGuid
        );
      })
      .addCase(updatePrivacySettingsEffect.fulfilled, (state, { payload }) => {
        state.profiles[payload.guid] = payload;
      })
      .addCase(
        fetchRepresentativeRolesEffect.fulfilled,
        (state, { payload }) => {
          state.representative.roles = payload;
        }
      )
      .addMatcher(
        isAnyOf(
          updateRepresentativeEffect.fulfilled,
          createRepresentativeEffect.fulfilled,
          deleteRepresentativeEffect.fulfilled
        ),
        (state, { payload }) => {
          if (!state.currentProfileGuid) {
            return;
          }

          const profile = state.profiles[state.currentProfileGuid];
          if (profile && payload) {
            profile.representatives = payload.representatives;
          }
        }
      );
  },
});

export const { setSearchNextLink, setLoadMore } = slice.actions;

export const profileReducer = slice.reducer;
